import { Box, ColorOptions, ModalDialog, Spacer, Text } from '@jfs/ui-new'
import { useFocusEffect } from '@react-navigation/native'
import React, { useCallback, useRef, useState } from 'react'
import { ExpandAddView } from '../../components/ExpandAddView'
import { ListNew } from '../../components/ListNew'
import { ModalAlertView } from '../../components/ModalAlertView'
import { useLazyApiData, useStores } from '../../data/store'
import { isAccessible } from '../../utils'
import { EditPackage } from './EditPackage'
import { FeePackageForm } from './FeePackageForm'

const FeePackage = () => {
  const [isModal, setModal] = useState(false)
  const [isExpand, setIsExpand] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [data, setData] = useState<any>({})

  const { getAll, packageList, accessConfig, update, deletePackage, config, exportFileAsync } = useStores((store) => ({
    getAll: store.feePackage.getAll,
    packageList: store.feePackage.getPackages(),
    length: store.feePackage.feePackages.length,
    update: store.feePackage.update,
    deletePackage: store.feePackage.deletePackage,
    config: store.user.accessConfig,
    accessConfig: store.user.getAccessConfig(['mdr', 'package']),
    exportFileAsync: store.exportFileAsync,
  }))

  const [lazyGetAll, { state, data: getAllApiData }] = useLazyApiData(getAll)
  const [lazyUpdate] = useLazyApiData(update)
  const [lazyDeletePackage] = useLazyApiData(deletePackage)
  const [lazyExportFileAsync, exportFileAsyncHandler] = useLazyApiData(exportFileAsync)
  const searchFormRef = React.useRef(null)

  useFocusEffect(
    useCallback(() => {
      if (isAccessible(accessConfig, 'fetch')) {
        lazyGetAll(searchFormRef?.current?.getValues() ?? {}, { page: 1, size: 10 })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazyGetAll, config]),
  )

  const keyExtractor = (item: any): string => {
    return item.id
  }

  const headTitles: any = [
    { id: 'ID' },
    { group: 'Group Name' },
    { category: 'Category' },
    { packageName: 'Package Name' },
  ]
  isAccessible(accessConfig, 'update') && headTitles.push({ edit: '' })
  isAccessible(accessConfig, 'delete') && headTitles.push({ delete: '' })

  const sizeConfig: any = [1.5, 3, 3, 3]
  isAccessible(accessConfig, 'update') && sizeConfig.push(2)
  isAccessible(accessConfig, 'delete') && sizeConfig.push(2)
  const onEdit = (item: any) => {
    setData(item)
    setModal(true)
  }

  const onDelete = (item: any) => {
    setData(item)
    setIsDeleteModalVisible(true)
  }

  const fetchDataFromServer = useCallback(
    (data, paginationInfo) => {
      lazyGetAll(data, paginationInfo)
    },
    [lazyGetAll],
  )
  const abortControllerRef = useRef<AbortController>()
  const getAbortController = () => {
    abortControllerRef.current = new AbortController()
    return abortControllerRef.current
  }

  const exportFile = () => {
    lazyExportFileAsync(
      { ...(searchFormRef?.current?.getValues() ?? {}), fileType: '1' },
      'PACKAGE_NAME_LIST',
      undefined,
      getAbortController,
    )
  }

  const getMappingList = () => {
    return (
      <Box>
        <ListNew
          searchFormRef={searchFormRef}
          searchData={SEARCH_FILTERS as any}
          data={getAllApiData?.content}
          updatedPaginationInfo={{
            ...(getAllApiData?.pageNo !== undefined && { page: getAllApiData?.pageNo }),
            ...(getAllApiData?.totalElements !== undefined && {
              total: getAllApiData?.totalElements,
            }),
          }}
          isSearchButtonLoading={state === ('pending' as any)}
          apiHandler={{ state, getAllApiData } as any}
          fetchDataFromServer={fetchDataFromServer}
          keyExtractor={keyExtractor}
          headTitle={headTitles}
          sizeConfig={sizeConfig}
          onEdit={onEdit}
          onDelete={onDelete}
          listTitle={'Package Name List'}
          onRowItemClick={undefined}
          exportFile={exportFile}
          isExportButtonLoading={exportFileAsyncHandler.state === 'pending'}
        />
      </Box>
    )
  }

  const onSuccess = () => {
    setIsExpand(false)
    setModal(false)
  }

  const handleUpdate = (updatedDate: any) => {
    lazyUpdate({
      id: updatedDate?.id,
      group: updatedDate?.group,
      packageName: updatedDate?.packageName,
      category: 'mdr',
      description: updatedDate?.description,
      fileType: 1,
    })
    setModal(false)
  }

  const handleDelete = () => {
    lazyDeletePackage({ ...data?.item, fileType: 1 })
    setIsDeleteModalVisible(false)
  }

  const renderModal = () => {
    if (!isModal) {
      return <></>
    }
    return (
      <EditPackage data={data?.item} update={handleUpdate} deletePackage={handleDelete} close={() => setModal(false)} />
    )
  }
  return (
    <Box flex={1}>
      <Text
        title={'Fee Package Creation Information'}
        fontSize={28}
        fontWeight={'600'}
        color={'#5a5c69' as ColorOptions}
      />
      <Spacer direction="vertical" size={24} />
      {isAccessible(accessConfig, 'add') && (
        <ExpandAddView
          children={
            <FeePackageForm
              data={data}
              onSuccess={onSuccess}
              isDeletable={isAccessible(accessConfig, 'delete')}
              isEditable={isAccessible(accessConfig, 'update')}
            />
          }
          isExpand={isExpand}
          setIsExpand={setIsExpand}
          title={'Create Package'}
        />
      )}
      <Spacer direction="vertical" size={16} />
      {isAccessible(accessConfig, 'fetch') && getMappingList()}

      <ModalDialog show={isModal} children={renderModal()} type={'Centered'} />
      <ModalAlertView
        show={isDeleteModalVisible}
        onDismiss={() => setIsDeleteModalVisible(false)}
        title="Delete Fee Package"
        message={`Is this final. This will raise request in approvals to delete routing package with package name ${data?.item?.packageName}`}
        onPress={handleDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        buttonTitle="Delete"
      />
    </Box>
  )
}

export default FeePackage

const SEARCH_FILTERS = [
  [
    { key: 'id', type: 'string', placeHolder: 'ID' },
    { key: 'group', type: 'string', placeHolder: 'Group' },
    { key: 'category', type: 'string', placeHolder: 'Category' },
    { key: 'packageName', type: 'string', placeHolder: 'Package Name' },
  ],
]
